import { FC, useMemo } from 'react';

import { useFormikContext } from 'formik';
import { Button, Tooltip } from 'ui/components/common';
import { Input } from 'ui/components/form';
import { SIZE } from 'utils/constants';
import { cn } from 'utils/helpers';
import { useNavigate } from 'react-router-dom';

import { CHANGE_EXPIRED_PASSWORD_FORM } from './schema';

interface PROPS {
  disabled?: boolean;
}

const PasswordExpiredForm: FC<PROPS> = ({ disabled }) => {
  const navigate = useNavigate();

  const {
    values,
    touched,
    errors,
    handleChange,
    handleBlur,
    handleSubmit,
    isSubmitting,
  } = useFormikContext<CHANGE_EXPIRED_PASSWORD_FORM>();

  const passed = useMemo(() => {
    if (
      !isSubmitting &&
      !Object.entries(errors).length &&
      values.password &&
      values.passwordConfirmation
    )
      return true;

    return false;
  }, [errors, isSubmitting, values.password, values.passwordConfirmation]);

  return (
    <form
      onSubmit={handleSubmit}
      className={cn(
        'w-[490px]',
        'px-[31px] py-[56px]',
        'shadow-custom rounded-[10px] bg-fill-tertiary',
      )}
    >
      <div className="space-y-4 mb-6">
        <p className="text-xl font-semibold text-center">Password Expired</p>
        <p className="text-sm font-thin text-center">
          It appears that your current password has expired. For your security,
          we require that you change your password every 90 days. Please create
          a new password to continue accessing your account.
        </p>
      </div>
      <div className="mb-3 space-y-3">
        <Tooltip
          title="Old Password must contain at least 8 characters including a number and a letter."
          theme="light"
          placement="right"
          className="w-full"
        >
          <Input
            className="w-full"
            type="password"
            name="oldPassword"
            size={SIZE.Large}
            textType={
              errors.oldPassword && touched.oldPassword ? 'danger' : 'default'
            }
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.oldPassword}
            placeholder="Enter current password"
            subText={
              errors.oldPassword && touched.oldPassword
                ? errors.oldPassword
                : ''
            }
            disabled={isSubmitting}
          />
        </Tooltip>
        <Tooltip
          title="Password must contain at least 8 characters including a number and a letter."
          theme="light"
          placement="right"
          className="w-full"
        >
          <Input
            className="w-full"
            type="password"
            name="password"
            size={SIZE.Large}
            textType={
              errors.password && touched.password ? 'danger' : 'default'
            }
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.password}
            placeholder="Enter new password"
            subText={errors.password && touched.password ? errors.password : ''}
            disabled={isSubmitting}
          />
        </Tooltip>
        <Input
          className="w-full"
          type="password"
          name="passwordConfirmation"
          size={SIZE.Large}
          textType={
            errors.passwordConfirmation && touched.passwordConfirmation
              ? 'danger'
              : 'default'
          }
          onChange={handleChange}
          onBlur={handleBlur}
          value={values.passwordConfirmation}
          placeholder="Re-enter new password"
          subText={
            errors.passwordConfirmation && touched.passwordConfirmation
              ? errors.passwordConfirmation
              : ''
          }
          disabled={isSubmitting}
        />
      </div>
      <Button
        label="CHANGE PASSWORD"
        type="submit"
        disabled={!passed || disabled || isSubmitting}
        className="w-full font-semibold mb-[15px]"
        size="lg"
      />
      <Button
        label="GO BACK TO LOGIN"
        type="button"
        intent="tertiary"
        className="w-full font-semibold mb-[15px]"
        size="lg"
        disabled={isSubmitting}
        onClick={() => navigate('/logout')}
      />
    </form>
  );
};

export default PasswordExpiredForm;
